import { useTheme } from "@emotion/react";
import {
	Autocomplete,
	Button,
	TextField,
	Grid,
	useMediaQuery,
	ButtonGroup,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import debounce from "lodash.debounce";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { renderCodeOption } from "../../utils/functions";
import { DispensableItemsTypes } from "./DispensableItems";
import PrintsSearch3 from "../../components/PrintSearch3";

const useStyles = makeStyles((theme) => ({
	sideMenuTitle: {
		fontSize: 20,
		fontWeight: 700,
		color: theme.palette.text.primary,
		marginBottom: "5px",
	},
	colorTitle: {
		color:
			theme.palette.mode === "dark"
				? "#fff !important"
				: "#000 !important",
	},
}));

const headers = [
	{ key: "trackingNumber", label: "Tracking" },
	{ key: "description", label: "Description" },
	{ key: "vendor", label: "Vendor" },
	{ key: "costFormatted", label: "Unit Cost" },
	{ key: "unitsInStock", label: "In Stock" },
	{ key: "maxStock", label: "Max" },
	{ key: "minBeforeRestock", label: "Min" },
];

const issuanceHeaders = [
	{ key: "date", label: "Date" },
	{ key: "trackingNumber", label: "Item" },
	{ key: "description", label: "Description" },
	{ key: "perPersonnelId", label: "Employee" },
	{ key: "vehicleId", label: "Vehicle" },
	{ key: "quantity", label: "Qty" },
	{ key: "serialId", label: "Serial/ID" },
	{ key: "totalCost", label: "Total Cost" },
	{ key: "isRestock", label: "Type" },
];

const DispensableItemsTableToolbar = ({
	openAdd,
	search,
	setSearch,
	onFilterChange,
	filters,
	resetFilters,
	itemType,
	setItemType,
	lowStockCount = 0,
	items = [],
	employees = [],
	vehicles = [],
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("md"));
	const btnStyles = {
		color: theme.palette.mode === "dark" ? "#fff" : "primary",
		borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
		"&:hover": {
			borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
			backgroundColor: theme.palette.action.hover,
		},
	};
	const columnsForPrint =
		itemType === DispensableItemsTypes.ISSUANCES
			? issuanceHeaders.map((h) => ({
					field: h.key,
					headerName: h.label,
					editable: false,
					flex: 1,
					...h,
				}))
			: headers.map((h) => ({
					field: h.key,
					headerName: h.label,
					editable: false,
					flex: 1,
					...h,
				}));

	const [localFilters, setLocalFilters] = useState(filters);
	const [printFlag, setPrintFlag] = useState(false);
	const [localSearch, setLocalSearch] = useState(search || "");

	useEffect(() => {
		setLocalFilters(filters);
	}, [filters]);

	useEffect(() => {
		setLocalSearch(search || "");
	}, [search]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearchChange = useCallback(
		debounce((searchString) => {
			setSearch(searchString);
		}, 500),
		[setSearch],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFilterChange = useCallback(
		debounce((newFilters) => {
			onFilterChange(newFilters);
		}, 500),
		[onFilterChange],
	);

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setLocalSearch(value);
		debouncedSearchChange(value);
	};

	const handleFilterChange = useCallback(
		(field, value) => {
			const updatedFilters = { ...localFilters, [field]: value };
			setLocalFilters(updatedFilters);
			debouncedFilterChange(updatedFilters);
		},
		[debouncedFilterChange, localFilters],
	);

	const handlePrintClick = () => {
		setPrintFlag(true);
		setTimeout(() => setPrintFlag(false), 1000);
	};

	// Get employee info for display
	const getEmployeeDisplay = useCallback(
		(employeeId) => {
			const employee = employees.find(
				(e) => e.value === employeeId || e.value === String(employeeId),
			);
			return employee ? employee.label : "—";
		},
		[employees],
	);

	const getVehicleDisplay = useCallback(
		(vehicleId) => {
			const vehicle = vehicles.find((v) => v.value === vehicleId);
			return vehicle ? vehicle.label : vehicleId || "—";
		},
		[vehicles],
	);

	// Prepare print data
	const printItems = useMemo(() => {
		return items.map((item) => ({
			...item,
			costFormatted:
				item.cost != null ? `$${Number(item.cost).toFixed(2)}` : "—",
		}));
	}, [items]);

	const issuancePrintItems = useMemo(() => {
		return items.map((item) => ({
			...item,
			date: item?.date ? new Date(item.date).toLocaleDateString() : "—",
			perPersonnelId: item.isRestock
				? "—"
				: getEmployeeDisplay(item.perPersonnelId),
			vehicleId: item.vehicleId ? getVehicleDisplay(item.vehicleId) : "—",
			isRestock: item.isRestock ? "RESTOCK" : "Issue",
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items]);

	// Get unique vendors for filter
	const vendorOptions = useMemo(() => {
		const vendors = [
			...new Set(items.map((i) => i.vendor).filter(Boolean)),
		];
		return vendors.map((v) => ({ label: v, value: v }));
	}, [items]);

	const renderFilterFields = (fields) =>
		fields.map(
			({ type, label, field, options, xs = 2, multiselect = true }) => (
				<Grid item xs={isSmall ? 12 : xs} key={field}>
					{type === "text" ? (
						<TextField
							label={label}
							value={localFilters[field] || ""}
							onChange={(e) =>
								handleFilterChange(field, e.target.value)
							}
							fullWidth
							variant="outlined"
							size="small"
						/>
					) : (
						<Autocomplete
							multiple={multiselect}
							options={options || []}
							value={
								multiselect
									? localFilters[field] || []
									: localFilters[field] || null
							}
							onChange={(_, value) =>
								handleFilterChange(field, value)
							}
							getOptionLabel={(option) => option?.label || ""}
							renderOption={renderCodeOption}
							isOptionEqualToValue={(o, v) =>
								o?.value === v?.value
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									variant="outlined"
									size="small"
								/>
							)}
							fullWidth
							sx={
								multiselect
									? {
											"& .MuiAutocomplete-inputRoot": {
												flexWrap: "nowrap",
												maxHeight: "40px",
											},
											"& .MuiAutocomplete-tag": {
												maxWidth: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											},
											"& .MuiAutocomplete-endAdornment": {
												alignSelf: "flex-start",
											},
										}
									: {}
							}
						/>
					)}
				</Grid>
			),
		);

	const filterFields = [
		{
			type: "autocomplete",
			label: "Vendors",
			field: "vendor",
			options: vendorOptions,
			xs: 1.5,
			multiselect: true,
		},
	];

	const buttons = [
		<Button
			key="items"
			variant={
				itemType === DispensableItemsTypes.ITEMS
					? "contained"
					: "outlined"
			}
			sx={itemType === DispensableItemsTypes.ITEMS ? {} : btnStyles}
			onClick={() => setItemType(DispensableItemsTypes.ITEMS)}
		>
			📦 Items
		</Button>,
		<Button
			key="restock"
			variant={
				itemType === DispensableItemsTypes.RESTOCK
					? "contained"
					: "outlined"
			}
			sx={itemType === DispensableItemsTypes.RESTOCK ? {} : btnStyles}
			onClick={() => setItemType(DispensableItemsTypes.RESTOCK)}
		>
			⚠️ Restock
			{lowStockCount > 0 && (
				<span
					style={{
						marginLeft: 5,
						background: "var(--red)",
						color: "#fff",
						borderRadius: 99,
						fontSize: 10,
						fontWeight: 700,
						padding: "1px 5px",
					}}
				>
					{lowStockCount}
				</span>
			)}
		</Button>,
		<Button
			key="issuances"
			variant={
				itemType === DispensableItemsTypes.ISSUANCES
					? "contained"
					: "outlined"
			}
			sx={itemType === DispensableItemsTypes.ISSUANCES ? {} : btnStyles}
			onClick={() => setItemType(DispensableItemsTypes.ISSUANCES)}
		>
			📋 Issuances
		</Button>,
	];

	return (
		<>
			<div>
				<div className={classes.sideMenuTitle}>Dispensable Items</div>
			</div>
			<div className="section-actions mb-2">
				<TextField
					label="Search tracking #, description..."
					type="search"
					className={classes.filter}
					variant="outlined"
					size="small"
					style={{
						width: "100%",
						maxWidth: 250,
					}}
					value={localSearch}
					onChange={handleSearchChange}
				/>
				{renderFilterFields(filterFields)}
				<ButtonGroup size="large" aria-label="Large button group">
					{buttons}
				</ButtonGroup>
				<Button
					variant="outlined"
					color="primary"
					onClick={handlePrintClick}
					title="Print current list"
					sx={btnStyles}
				>
					🖨️ Print
				</Button>
				<Button variant="contained" color="primary" onClick={openAdd}>
					+ New Item
				</Button>
			</div>

			<PrintsSearch3
				title="Dispensable Items"
				data={
					itemType === DispensableItemsTypes.ISSUANCES
						? issuancePrintItems
						: printItems
				}
				cols={columnsForPrint}
				print={printFlag}
			/>
		</>
	);
};

export default DispensableItemsTableToolbar;
